import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Nouveau logo
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/t04kcxdw_Adobe%20Express%20-%20file-modified.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/nos-tarifs', label: 'Nos Tarifs' },
    { href: '/rendez-vous', label: 'Rendez-vous' },
    { href: '/realisations', label: 'Réalisations' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 backdrop-blur-md border-b border-gold/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="Aldroubi Brico" className="h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                  isActive(link.href)
                    ? 'text-gold'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons - Sans numéro */}
          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 px-3 py-1.5 rounded">
              <Clock className="w-3 h-3" />
              24h/24
            </div>
            <Link
              to="/nos-tarifs"
              className="btn-gold text-xs px-5 py-2.5"
              data-testid="navbar-devis"
            >
              Devis Gratuit
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-white p-2"
            data-testid="navbar-mobile-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0A0A0A] border-t border-gold/20"
          >
            <div className="px-4 py-6 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`block py-3 px-4 text-base font-medium rounded transition-colors ${
                    isActive(link.href) 
                      ? 'text-gold bg-gold/10' 
                      : 'text-gray-300 hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 mt-4 border-t border-white/10 space-y-4">
                <div className="flex items-center gap-2 text-sm text-green-400 px-4">
                  <Clock className="w-4 h-4" />
                  Disponible 24h/24 - 7j/7
                </div>
                <Link
                  to="/nos-tarifs"
                  onClick={() => setIsOpen(false)}
                  className="btn-gold block text-center mx-4"
                >
                  Devis Gratuit
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
